/**
 * Pins this device has reported — the visible half of a report (App
 * Store Guideline 1.2b).
 *
 * The server side of a report is immediate and total: the first report
 * stamps `quarantined_at`, deletes the DynamoDB observation row, and
 * drops the submission out of the label queue, the detail endpoint and
 * the public export. What it CANNOT do is reach into the baked map
 * tiles — leaf geometry at high zoom comes from PMTiles, and the bake is
 * a by-hand script run (`scripts/moderation-review.mjs` says so in its
 * own docblock). So the one moderation surface a reviewer finds without
 * hunting — "Report this pin" on the map — used to end with the reporter
 * being told the photo was out of public view while the pin sat exactly
 * where it was, still opening, still offering to report itself again.
 *
 * This store closes that gap on the client: the ids reported from this
 * device, so the tile renderer can drop them the moment the report
 * lands. It is the mirror image of own-pins.ts (which ADDS a pin the
 * tiles don't have yet); same localStorage idiom, same pruning, opposite
 * sign.
 *
 * It is deliberately NOT authority. Everyone else's copy of the map
 * loses the pin at the next bake; this only spares the reporter the lie.
 *
 * RETENTION — 30 days, longer than own-pins' 24h and for a different
 * reason. A bake runs far more often than monthly, so a reported pin
 * that is still in the tiles a month later is one a person LOOKED at and
 * restored (`--dismiss`). Hiding it past that point would be this device
 * quietly overriding a review decision, which is not what the reporter
 * asked for.
 */

import { observationIdsFor } from './recent-submissions';

const STORAGE_KEY = 'trail.reported_obs_ids';
const MAX_ENTRIES = 200;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

interface ReportedPin {
  /**
   * As the reporting surface named it: the /label queue sends a bare
   * submission ULID, the map sends `sub-<ulid>`. Both are stored
   * verbatim and expanded through observationIdsFor() on read — the same
   * both-forms rule recent-submissions.ts uses, and for the same reason.
   */
  id: string;
  /** Epoch ms the report was accepted (201) or found on file (409). */
  ts: number;
}

function prune(entries: ReportedPin[], now: number): ReportedPin[] {
  return entries
    .filter((e) => e && typeof e.id === 'string' && typeof e.ts === 'number')
    .filter((e) => now - e.ts <= MAX_AGE_MS)
    .slice(-MAX_ENTRIES);
}

function readEntries(now: number): ReportedPin[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return prune(parsed as ReportedPin[], now);
  } catch {
    return [];
  }
}

/**
 * Every observation-feature id this device has reported, in both id
 * conventions, ready to test against a map feature's `id`.
 */
export function readReportedPinIds(now: number = Date.now()): Set<string> {
  return new Set(readEntries(now).flatMap((e) => observationIdsFor(e.id)));
}

/**
 * Record a report. Called from ReportDialog on BOTH outcomes that mean
 * "this device has reported this": 201 (filed now) and 409 (already on
 * file from an earlier attempt). Storage failure is swallowed — the
 * report itself already committed server-side, and a private-mode
 * browser must not turn that into an error the user sees.
 */
export function recordReportedPin(id: string, now: number = Date.now()): void {
  if (typeof window === 'undefined') return;
  if (!id) return;
  const entries = readEntries(now).filter((e) => e.id !== id);
  entries.push({ id, ts: now });
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prune(entries, now)));
  } catch {
    /* storage full / privacy mode — suppression is best-effort */
  }
}
